import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, CircleMarker, Popup, Tooltip, useMap } from 'react-leaflet';
import { MASTER_DAPIL_DATA } from '../../lib/dapilMasterData';
import { KECAMATAN_COORDS } from '../../lib/kecamatanCoords';
import { formatNumber } from './UI';
import { Layers, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

// Dapil color styling (Tailwind-compatible hex)
export const DAPIL_PALETTE = {
  'Dapil 1': { color: '#EF4444', label: 'Dapil 1', kursi: '1 Kursi', badge: 'bg-red-500' },
  'Dapil 2': { color: '#F59E0B', label: 'Dapil 2', kursi: '1 Kursi', badge: 'bg-amber-500' },
  'Dapil 3': { color: '#3B82F6', label: 'Dapil 3', kursi: '2 Kursi ⭐', badge: 'bg-blue-500' },
  'Dapil 4': { color: '#10B981', label: 'Dapil 4', kursi: '3 Kursi ⭐⭐', badge: 'bg-emerald-500' },
  'Dapil 5': { color: '#8B5CF6', label: 'Dapil 5', kursi: '1 Kursi', badge: 'bg-purple-500' },
  'Dapil 6': { color: '#F97316', label: 'Dapil 6', kursi: '1 Kursi', badge: 'bg-orange-500' },
};

const COORDS_MAP = Object.fromEntries(
  KECAMATAN_COORDS.map(k => [k.name.toLowerCase(), k.coords])
);

// Convex Hull 2D (Monotone chain) to create territory polygon around kecamatan points
function computeConvexHull(points) {
  if (!points || points.length <= 2) return points || [];
  const pts = points.slice().sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

// Slightly expand hull points outward from centroid for better territory coverage
function expandHull(hullPoints, factor = 0.035) {
  if (!hullPoints || hullPoints.length < 3) return hullPoints;
  const latSum = hullPoints.reduce((acc, p) => acc + p[0], 0);
  const lngSum = hullPoints.reduce((acc, p) => acc + p[1], 0);
  const centerLat = latSum / hullPoints.length;
  const centerLng = lngSum / hullPoints.length;

  return hullPoints.map(([lat, lng]) => {
    const dLat = lat - centerLat;
    const dLng = lng - centerLng;
    const dist = Math.sqrt(dLat * dLat + dLng * dLng) || 1;
    return [
      lat + (dLat / dist) * factor,
      lng + (dLng / dist) * factor,
    ];
  });
}

// Controller to handle animated zooming and view bounds
const MapViewController = ({ targetBounds, targetCenter, zoomLevel }) => {
  const map = useMap();

  useEffect(() => {
    if (targetBounds && targetBounds.length > 0) {
      try {
        map.fitBounds(targetBounds, { padding: [25, 25], maxZoom: 11, animate: true, duration: 0.8 });
      } catch (e) {
        // Fallback
        if (targetCenter) map.setView(targetCenter, zoomLevel || 9);
      }
    } else if (targetCenter) {
      map.setView(targetCenter, zoomLevel || 9, { animate: true, duration: 0.8 });
    }
  }, [map, targetBounds, targetCenter, zoomLevel]);

  return null;
};

const KAB_SUKABUMI_CENTER = [-7.02, 106.72];

const PetaDapilLeaflet = ({
  height = 260,
  selectedDapil = null,
  onSelectDapil = null,
  showControls = true,
  showLegend = true,
  className = '',
}) => {
  const [activeDapil, setActiveDapil] = useState(selectedDapil || 'all');
  const [mapLayer, setMapLayer] = useState('google'); // 'google' | 'satellite' | 'topo'

  useEffect(() => {
    if (selectedDapil) {
      setActiveDapil(selectedDapil);
    }
  }, [selectedDapil]);

  const handleDapilClick = (dapilName) => {
    const next = activeDapil === dapilName ? 'all' : dapilName;
    setActiveDapil(next);
    if (onSelectDapil) {
      onSelectDapil(next === 'all' ? null : next);
    }
  };

  // Pre-process Dapils with coordinates and convex hull
  const dapilsWithData = useMemo(() => {
    return MASTER_DAPIL_DATA.map((d) => {
      const kecsWithCoords = d.kecamatan
        .map((k) => ({
          ...k,
          coords: COORDS_MAP[k.nama.toLowerCase()],
        }))
        .filter((k) => k.coords);

      const points = kecsWithCoords.map((k) => k.coords);
      const rawHull = computeConvexHull(points);
      const expandedPolygon = expandHull(rawHull, 0.038);

      const palette = DAPIL_PALETTE[d.dapil] || { color: '#6B7280' };

      return {
        ...d,
        color: palette.color,
        kecamatanList: kecsWithCoords,
        polygonPositions: expandedPolygon,
        bounds: points,
      };
    });
  }, []);

  // Compute view target
  const { targetBounds, targetCenter, zoomLevel } = useMemo(() => {
    if (activeDapil && activeDapil !== 'all') {
      const target = dapilsWithData.find((d) => d.dapil === activeDapil);
      if (target && target.bounds.length > 0) {
        return {
          targetBounds: target.bounds,
          targetCenter: null,
          zoomLevel: 10,
        };
      }
    }
    return {
      targetBounds: null,
      targetCenter: KAB_SUKABUMI_CENTER,
      zoomLevel: 9,
    };
  }, [activeDapil, dapilsWithData]);

  const tileUrl =
    mapLayer === 'satellite'
      ? 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'
      : mapLayer === 'topo'
      ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'
      : 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}';

  const tileAttribution =
    mapLayer === 'topo'
      ? '&copy; Esri &mdash; OpenStreetMap contributors'
      : '&copy; Google Maps';

  return (
    <div className={`flex flex-col w-full ${className}`}>
      {/* Top Filter Buttons / Header */}
      {showControls && (
        <div className="flex items-center justify-between gap-1.5 mb-2 overflow-x-auto pb-1 text-xs">
          <div className="flex items-center gap-1 overflow-x-auto">
            <button
              type="button"
              onClick={() => handleDapilClick('all')}
              className={`px-2 py-0.5 rounded-lg text-[11px] font-bold transition-all flex-shrink-0 ${
                activeDapil === 'all'
                  ? 'bg-gray-900 text-white shadow-xs'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Semua Dapil
            </button>
            {MASTER_DAPIL_DATA.map((d) => {
              const pal = DAPIL_PALETTE[d.dapil];
              const isSelected = activeDapil === d.dapil;
              return (
                <button
                  key={d.dapil}
                  type="button"
                  onClick={() => handleDapilClick(d.dapil)}
                  className={`px-2 py-0.5 rounded-lg text-[11px] font-bold transition-all flex-shrink-0 flex items-center gap-1 ${
                    isSelected
                      ? 'text-white shadow-xs scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={{
                    backgroundColor: isSelected ? pal.color : undefined,
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full inline-block"
                    style={{ backgroundColor: isSelected ? '#fff' : pal.color }}
                  />
                  {d.dapil}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                const next = mapLayer === 'google' ? 'satellite' : mapLayer === 'satellite' ? 'topo' : 'google';
                setMapLayer(next);
              }}
              title="Ganti Tampilan Peta (Google Maps / Satelit / Esri)"
              className="px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200 flex items-center gap-1"
            >
              <Layers className="w-2.5 h-2.5" />
              <span>{mapLayer === 'google' ? 'Google' : mapLayer === 'satellite' ? 'Satelit' : 'Street'}</span>
            </button>
            {activeDapil !== 'all' && (
              <button
                type="button"
                onClick={() => handleDapilClick('all')}
                title="Reset Tampilan"
                className="p-1 text-gray-500 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
              >
                <RotateCcw className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Leaflet Map Canvas Container */}
      <div
        className="relative rounded-xl overflow-hidden border border-gray-200 shadow-inner w-full bg-slate-100"
        style={{ height }}
      >
        <MapContainer
          center={KAB_SUKABUMI_CENTER}
          zoom={9}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer url={tileUrl} attribution={tileAttribution} />

          <MapViewController
            targetBounds={targetBounds}
            targetCenter={targetCenter}
            zoomLevel={zoomLevel}
          />

          {/* Render Dapil Polygons */}
          {dapilsWithData.map((d) => {
            const isSelected = activeDapil === d.dapil;
            const isDimmed = activeDapil !== 'all' && !isSelected;

            return (
              <Polygon
                key={`poly-${d.dapil}`}
                positions={d.polygonPositions}
                pathOptions={{
                  color: d.color,
                  fillColor: d.color,
                  fillOpacity: isDimmed ? 0.08 : isSelected ? 0.35 : 0.22,
                  weight: isSelected ? 3 : 1.5,
                  dashArray: isDimmed ? '4, 4' : undefined,
                }}
                eventHandlers={{
                  click: () => handleDapilClick(d.dapil),
                }}
              >
                <Tooltip direction="center" permanent={false} opacity={0.95}>
                  <div className="text-center font-bold text-xs">
                    <span style={{ color: d.color }}>{d.nama}</span>
                    <div className="text-[10px] text-gray-600 font-medium">
                      Target: {d.target_kursi_2029} Kursi ({formatNumber(d.target_suara_2029)} suara)
                    </div>
                  </div>
                </Tooltip>
              </Polygon>
            );
          })}

          {/* Render Kecamatan Markers */}
          {dapilsWithData.map((d) => {
            const isSelected = activeDapil === d.dapil;
            const isDimmed = activeDapil !== 'all' && !isSelected;

            return d.kecamatanList.map((kec) => {
              const markerRadius = isSelected ? 8 : activeDapil === 'all' ? 6 : 5;

              return (
                <CircleMarker
                  key={`marker-${d.dapil}-${kec.nama}`}
                  center={kec.coords}
                  radius={markerRadius}
                  pathOptions={{
                    color: '#ffffff',
                    fillColor: d.color,
                    fillOpacity: isDimmed ? 0.25 : 1,
                    weight: 2,
                  }}
                  eventHandlers={{
                    click: () => {
                      if (activeDapil !== d.dapil) {
                        handleDapilClick(d.dapil);
                      }
                    },
                  }}
                >
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs">
                      <b className="text-gray-900">{kec.nama}</b>
                      <span className="text-gray-500 text-[10px] block">
                        {d.dapil} · Target: {formatNumber(kec.target_2029)}
                      </span>
                    </div>
                  </Tooltip>

                  <Popup>
                    <div className="p-1 text-xs min-w-[190px]">
                      <div className="flex items-center justify-between gap-2 border-b pb-1.5 mb-1.5">
                        <span className="font-extrabold text-sm text-gray-900">{kec.nama}</span>
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-bold text-white"
                          style={{ backgroundColor: d.color }}
                        >
                          {d.dapil}
                        </span>
                      </div>
                      <div className="space-y-1 text-gray-600 text-[11px]">
                        <div className="flex justify-between">
                          <span>Target 2029:</span>
                          <b className="text-orange-600">{formatNumber(kec.target_2029)} suara</b>
                        </div>
                        <div className="flex justify-between">
                          <span>Baseline 2024:</span>
                          <b>{formatNumber(kec.baseline_2024)} suara</b>
                        </div>
                        <div className="flex justify-between">
                          <span>Gap Suara:</span>
                          <b className="text-amber-700">+{formatNumber(kec.gap)}</b>
                        </div>
                        <div className="flex justify-between pt-1 border-t border-gray-100">
                          <span>Target Dapil:</span>
                          <b className="text-gray-900">{d.target_kursi_2029} Kursi</b>
                        </div>
                      </div>

                      {onSelectDapil && (
                        <button
                          type="button"
                          onClick={() => onSelectDapil(d.dapil)}
                          className="w-full mt-2.5 py-1 px-2 text-center text-[10px] font-bold text-white rounded bg-orange-600 hover:bg-orange-700 transition"
                        >
                          Pilih {d.dapil} →
                        </button>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              );
            });
          })}
        </MapContainer>

        {/* Floating badge for free OpenStreetMap info */}
        <div className="absolute bottom-1 right-1 bg-white/90 backdrop-blur-xs px-1.5 py-0.5 rounded text-[9px] font-medium text-gray-500 z-[1000] border border-gray-200 pointer-events-none">
          OpenStreetMap · 47 Kecamatan
        </div>
      </div>

      {/* Legend Mini 6 Dapil at bottom */}
      {showLegend && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 w-full mt-2 text-[10px] font-bold text-gray-700">
          {MASTER_DAPIL_DATA.map((d) => {
            const pal = DAPIL_PALETTE[d.dapil];
            const isSelected = activeDapil === d.dapil;
            return (
              <div
                key={d.dapil}
                onClick={() => handleDapilClick(d.dapil)}
                className={`flex items-center gap-1 p-1 rounded cursor-pointer transition ${
                  isSelected ? 'bg-orange-50 ring-1 ring-orange-300' : 'hover:bg-gray-50'
                }`}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: pal.color }}></span>
                <span className="truncate">{d.dapil} ({d.target_kursi_2029}K)</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PetaDapilLeaflet;

